import { Search } from "lucide-react";
import {
  forwardRef,
  type ComponentProps,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

import { cn } from "@/lib/utils/cn";

const controlStateClass =
  "aria-[invalid=true]:border-status-error aria-[invalid=true]:shadow-[0_0_0_3px_var(--semantic-error-surface)]";

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cn("ui-control w-full", controlStateClass, className)} {...props} />;
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cn("ui-control min-h-[10rem] w-full resize-y text-body leading-relaxed", controlStateClass, className)}
      {...props}
    />
  );
});

export type SearchInputProps = InputProps & { containerClassName?: string };

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { className, containerClassName, type = "search", ...props },
  ref,
) {
  return (
    <span className={cn("relative block", containerClassName)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
      <Input ref={ref} type={type} className={cn("pl-10", className)} {...props} />
    </span>
  );
});

export const Label = forwardRef<HTMLLabelElement, ComponentProps<"label"> & { required?: boolean }>(
  function Label({ className, children, required, ...props }, ref) {
    return (
      <label ref={ref} className={cn("block text-micro uppercase tracking-[0.12em] text-muted", className)} {...props}>
        {children}
        {required ? <span className="text-status-error"> *</span> : null}
      </label>
    );
  },
);

export type FieldGroupProps = {
  label: string;
  id: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
  className?: string;
};

export function FieldGroup({ label, id, hint, error, required, children, className }: FieldGroupProps) {
  const messageId = `${id}-message`;
  return (
    <div className={cn("space-y-ds-2", className)} data-invalid={Boolean(error) || undefined}>
      <Label htmlFor={id} required={required}>{label}</Label>
      {children}
      {hint && !error ? <p id={messageId} className="text-caption text-muted">{hint}</p> : null}
      {error ? <p id={messageId} role="alert" className="text-caption text-status-error">{error}</p> : null}
    </div>
  );
}
